import React from 'react';

import {
  BUSINESS_VALIDATION_STATUSES,
  BUSINESS_VALIDATION_TYPE,
} from '~/components/Contacts/businessValidationStatuses';
import type {
  GbgBusinessRetrieveValidation,
  GbgBusinessValidationDetails,
} from '~/components/core/AddressAutocomplete/GbgAddressAutocomplete/types';
import Tooltip from '~/components/core/Atomic/Tooltip';
import type { StatusChipProps } from '~/components/core/StatusChipCore';
import StatusChipCore from '~/components/core/StatusChipCore';
import Text from '~/components/core/TextComponents/Text';
import CheckIcon from '~/components/icons/CheckIcon';
import RejectedIcon from '~/components/icons/RejectedIcon';
import WarningCircleIcon from '~/components/icons/WarningCircle';
import { serverDateToLocal } from '~/DateTimeUtils';
import type { ContactInfoContactValidationInfo } from '~/server_shared/openapi/data-contracts';
import cn from '~/Utils/cn';

import { getBadgeTexts, getTooltipTexts } from './texts';

const getBadgeConfig = (status: string, country: string): StatusChipProps => {
  const texts = getBadgeTexts(country);

  switch (status) {
    case BUSINESS_VALIDATION_STATUSES.BN_VERIFIED:
      return { status: texts.verified, color: 'green', Icon: CheckIcon };
    case BUSINESS_VALIDATION_STATUSES.BN_VERIFIED_NO_GST:
      return { status: texts.verifiedNoGst, color: 'orange', Icon: CheckIcon };
    case BUSINESS_VALIDATION_STATUSES.BN_VERIFIED_GST_UNKNOWN:
      return { status: texts.verified, color: 'green', Icon: CheckIcon };
    case BUSINESS_VALIDATION_STATUSES.BN_CANCELLED:
      return { status: texts.cancelled, color: 'red', Icon: RejectedIcon };
    case BUSINESS_VALIDATION_STATUSES.BN_INVALID:
      return { status: texts.invalid, color: 'red', Icon: RejectedIcon };
    case BUSINESS_VALIDATION_STATUSES.NOT_VALIDATED:
    default:
      return { status: texts.notValidated, color: 'grey', Icon: WarningCircleIcon };
  }
};

function getBusinessValidation(
  validations?: ContactInfoContactValidationInfo[]
): ContactInfoContactValidationInfo | undefined {
  return validations?.find((v) => v.validation_type === BUSINESS_VALIDATION_TYPE);
}

interface TooltipContentProps {
  details: GbgBusinessValidationDetails;
  formattedDate?: string;
  country: string;
}

const TooltipRow: React.FC<{ label: string; value: string }> = ({ label, value }) => {
  return (
    <div>
      <span className="font-semibold">{label}:</span> {value}
    </div>
  );
};

const VerifiedTooltipContent: React.FC<TooltipContentProps> = ({ details, formattedDate, country }) => {
  const texts = getTooltipTexts(country);

  return (
    <Text
      className="p-4"
      variant={Text.VARIANTS.XS}
      weight={Text.WEIGHTS.REGULAR}
      colorVariant={Text.COLOR_VARIANTS.WHITE}
    >
      {details.name ? <TooltipRow label={texts.name} value={details.name} /> : null}
      {details.bn ? <TooltipRow label={texts.bn} value={details.bn} /> : null}
      {details.entity_type ? <TooltipRow label={texts.entityType} value={details.entity_type} /> : null}
      <TooltipRow label={texts.gstStatus} value={details.gst_status || texts.gstStatusFallback} />
      {formattedDate ? <TooltipRow label={texts.verifiedOn} value={formattedDate} /> : null}
    </Text>
  );
};

const TOOLTIP_STATUSES: ReadonlySet<string> = new Set([
  BUSINESS_VALIDATION_STATUSES.BN_VERIFIED,
  BUSINESS_VALIDATION_STATUSES.BN_VERIFIED_NO_GST,
  BUSINESS_VALIDATION_STATUSES.BN_VERIFIED_GST_UNKNOWN,
  BUSINESS_VALIDATION_STATUSES.BN_CANCELLED,
]);

interface BusinessVerificationBadgeProps {
  validations?: ContactInfoContactValidationInfo[];
  sessionValidation?: GbgBusinessRetrieveValidation;
  country?: string;
  className?: string;
}

const BusinessVerificationBadge: React.FC<BusinessVerificationBadgeProps> = ({
  validations,
  sessionValidation,
  country = 'AU',
  className,
}) => {
  const dbValidation = getBusinessValidation(validations);
  const effectiveStatus =
    sessionValidation?.status ?? dbValidation?.status ?? BUSINESS_VALIDATION_STATUSES.NOT_VALIDATED;
  const badgeConfig = getBadgeConfig(effectiveStatus, country);

  const sessionDetails =
    sessionValidation?.validation_details?.type === BUSINESS_VALIDATION_TYPE
      ? sessionValidation.validation_details
      : null;
  const dbDetails =
    dbValidation?.validation_details?.type === BUSINESS_VALIDATION_TYPE ? dbValidation.validation_details : null;
  const details = sessionDetails ?? dbDetails;
  const formattedDate = !sessionValidation && dbValidation ? serverDateToLocal(dbValidation.updated_at) : undefined;

  const mainBadge = (
    <div data-testid="business-verification-badge" className="inline-block">
      <StatusChipCore color={badgeConfig.color} status={badgeConfig.status} Icon={badgeConfig.Icon} />
    </div>
  );

  let wrappedBadge = mainBadge;
  if (details && TOOLTIP_STATUSES.has(effectiveStatus)) {
    wrappedBadge = (
      <Tooltip
        title={<VerifiedTooltipContent details={details} formattedDate={formattedDate} country={country} />}
        enterDelay={400}
        enterNextDelay={400}
      >
        {mainBadge}
      </Tooltip>
    );
  }

  return <div className={cn('-ms-4 flex flex-wrap gap-8', className)}>{wrappedBadge}</div>;
};

export default BusinessVerificationBadge;
